// StatusIndicator — Presentational status primitive for the chronological
// timeline redesign.
//
// Replicates the CloudScape `StatusIndicator type=...` visual using a
// `lucide-react` icon plus a `designTokens` color, mapping a Step_Status
// ('completed' | 'in-progress' | 'upcoming') to its label, icon, and color.
// Rendered in the first column of each TripStep to communicate where the
// traveler is in their journey.
//
// Status map (per design):
//   completed   → "Completed"   / CheckCircle / colors.success
//   in-progress → "In progress" / Loader      / colors.info
//   upcoming    → "Upcoming"    / Circle       / colors.textSecondary
//
// Validates: Requirements 4.2, 4.3, 4.4

import React from 'react';
import { CheckCircle, Loader, Circle } from 'lucide-react';

import { colors, typography, fontWeights } from '../../../styles/designTokens';

/**
 * @typedef {'completed'|'in-progress'|'upcoming'} StepStatus
 */

/**
 * Maps a Step_Status to its label, lucide icon component, and color token.
 * Falls back to the `upcoming` (pending) treatment for unknown statuses.
 *
 * @param {StepStatus} status
 * @returns {{ label: string, Icon: React.ComponentType, color: string }}
 */
function getStatusConfig(status) {
  switch (status) {
    case 'completed':
      return { label: 'Completed', Icon: CheckCircle, color: colors.success };
    case 'in-progress':
      return { label: 'In progress', Icon: Loader, color: colors.info };
    case 'upcoming':
    default:
      return { label: 'Upcoming', Icon: Circle, color: colors.textSecondary };
  }
}

/**
 * @typedef {Object} StatusIndicatorProps
 * @property {StepStatus} status - The temporal status of the step
 */

/**
 * StatusIndicator renders an icon + label pair communicating step status.
 *
 * @param {StatusIndicatorProps} props
 * @returns {React.ReactElement}
 */
export function StatusIndicator({ status }) {
  const { label, Icon, color } = getStatusConfig(status);

  return (
    <span
      data-testid="status-indicator"
      data-status={status}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        color,
        fontSize: typography.caption.fontSize,
        fontWeight: fontWeights.medium,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={14} color={color} aria-hidden="true" />
      {label}
    </span>
  );
}

export default StatusIndicator;
